import {
  Box,
  Typography,
  useTheme,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  type GridColumns,
  type GridRowData,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { strings } from 'i18n/Strings';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { viewAllWithdrawalRequests } from 'service/RequestsService';
import { colorTheme } from 'theme';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import { widrawalStatusTypeOption } from 'constants/constantText';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';

interface PlanRequests {
  requests: Request[];
}

interface Request {
  requestId: string;
  status: string;
  user: User;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
}

const WithdrawalRequests = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [planData, setPlanData] = useState<Request[]>([]);
  const [pageCount, setPageCount] = useState(null);
  const [filters, setFilters] = useState<unknown>({
    startDate: null,
    endDate: null,
    status: '',
  });
  const [searchValue, setSearchValue] = useState('');

  const pageInfo = useSelector(
    (state) => state?.pagination?.withdrawalRequests,
  );

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const getAllWithdrawalRequests = async (): Promise<void> => {
    try {
      const withdrawalRequests = (await viewAllWithdrawalRequests(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
        filters,
      )) as PlanRequests;
      setPlanData(withdrawalRequests?.requests?.rows);
      setPageCount(withdrawalRequests?.requests?.count);
    } catch (error) {}
  };

  useEffect(() => {
    void getAllWithdrawalRequests();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleApplyFilter = async (): Promise<void> => {
    dispatch(setCurrentPage({ key: 'withdrawalRequests', value: 0 }));

    void getAllWithdrawalRequests();
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'requestId',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.withdrawRequestId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestId}
        </Typography>
      ),
    },
    {
      field: 'user.firstName',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.name}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.firstName} {row.user.lastName}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'status',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status === 'In-progress' ? 'In-Progress' : row.status}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={5} />
        <Grid sx={{ minWidth: 150, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 150, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid sx={{ minWidth: 170, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select-Status"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {widrawalStatusTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={() => {
              void handleApplyFilter();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>
      <CustomDataGrid
        columns={columns}
        rows={planData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.requestId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        onRowClick={(row: GridRowData) => {
          sessionStorage.setItem('selectedRequestTab', 'withdrawalRequests');
          navigate(`/user-request-details/${Number(row.id)}`);
        }}
        currentPage="withdrawalRequests"
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel2}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(
            setSortBy({
              key: 'withdrawalRequests',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'withdrawalRequests',
              value: mode[0].sort,
            }),
          );
          dispatch(setCurrentPage({ key: 'withdrawalRequests', value: 0 }));
        }}
      />
    </Box>
  );
};

export default WithdrawalRequests;
